import React, { useEffect, useState } from "react";
import moment from "moment";
import Pagination from "react-js-pagination";
import {
    Button, Card, CardHeader, CardBody, CardTitle,
    Table, Container, Row, Col, Spinner, Nav, NavItem,
    NavLink, TabContent, TabPane, Badge
} from "reactstrap";
import membershipRemindersService from "../../services/api/membership-reminders-service";

const RECORDS_PER_PAGE = 20;

const TABS = [
    { key: "expiringSoon", label: "Expiring Soon (T-7)" },
    { key: "expiringToday", label: "Expiring Today" },
    { key: "recentlyExpired", label: "Recently Expired (T+7)" },
    { key: "inactive", label: "Inactive Subscribers" },
];

const statusColor = {
    expiringSoon: "warning",
    expiringToday: "danger",
    recentlyExpired: "secondary",
    inactive: "dark",
};

const SubscriberTable = ({ subscriptions, loading }) => {
    if (loading) {
        return (
            <div className="text-center p-4">
                <Spinner color="primary" />
            </div>
        );
    }
    if (!subscriptions || subscriptions.length === 0) {
        return <p className="text-center text-muted p-4">No subscribers found.</p>;
    }
    return (
        <Table responsive striped>
            <thead className="text-primary">
                <tr>
                    <th>#</th>
                    <th>User Email</th>
                    <th>User Name</th>
                    <th>Project</th>
                    <th>Status</th>
                    <th>Subscription End</th>
                    <th>Platform</th>
                </tr>
            </thead>
            <tbody>
                {subscriptions.map((sub, idx) => (
                    <tr key={sub._id}>
                        <td>{idx + 1}</td>
                        <td>{sub.user_id?.email || '-'}</td>
                        <td>{sub.user_id?.name || '-'}</td>
                        <td>{sub.project_id?.name || '-'}</td>
                        <td>
                            <Badge color={sub.status === 'expired' ? 'danger' : 'success'}>
                                {sub.status}
                            </Badge>
                        </td>
                        <td>{sub.current_end ? moment.unix(sub.current_end).format("DD MMM YYYY") : '-'}</td>
                        <td>{sub.platform || '-'}</td>
                    </tr>
                ))}
            </tbody>
        </Table>
    );
};

const ReminderDashboard = () => {
    const [activeTab, setActiveTab] = useState("expiringSoon");
    const [data, setData] = useState({
        expiringSoon: { list: [], count: 0 },
        expiringToday: { list: [], count: 0 },
        recentlyExpired: { list: [], count: 0 },
        inactive: { list: [], count: 0 },
    });
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);

    const fetchData = async (tab, pageNum = 1) => {
        setLoading(true);
        try {
            let result;
            if (tab === "expiringSoon") result = await membershipRemindersService.getExpiringSoon(null, pageNum, RECORDS_PER_PAGE);
            else if (tab === "expiringToday") result = await membershipRemindersService.getExpiringToday(null, pageNum, RECORDS_PER_PAGE);
            else if (tab === "recentlyExpired") result = await membershipRemindersService.getRecentlyExpired(null, pageNum, RECORDS_PER_PAGE);
            else if (tab === "inactive") result = await membershipRemindersService.getInactiveSubscribers(null, pageNum, RECORDS_PER_PAGE);

            if (result && result.success) {
                setData(prev => ({
                    ...prev,
                    [tab]: { list: result.subscriptions, count: result.count }
                }));
            }
        } catch (err) {
            console.error("Error fetching reminder data:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(activeTab, page);
    }, [activeTab, page]);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setPage(1);
    };

    const currentData = data[activeTab];

    return (
        <Container fluid>
            <Row>
                <Col>
                    <Card>
                        <CardHeader>
                            <CardTitle tag="h4">Subscription Reminder Dashboard</CardTitle>
                            <p className="card-category text-muted">
                                Monitor subscribers approaching expiry and recently churned users
                            </p>
                        </CardHeader>
                        <CardBody>
                            <Nav tabs>
                                {TABS.map(tab => (
                                    <NavItem key={tab.key}>
                                        <NavLink
                                            className={activeTab === tab.key ? "active" : ""}
                                            onClick={() => handleTabChange(tab.key)}
                                            style={{ cursor: "pointer" }}
                                        >
                                            {tab.label}
                                            {data[tab.key].count > 0 && (
                                                <Badge color={statusColor[tab.key]} className="ml-2">
                                                    {data[tab.key].count}
                                                </Badge>
                                            )}
                                        </NavLink>
                                    </NavItem>
                                ))}
                            </Nav>

                            <TabContent activeTab={activeTab} className="mt-3">
                                {TABS.map(tab => (
                                    <TabPane tabId={tab.key} key={tab.key}>
                                        <SubscriberTable
                                            subscriptions={currentData.list}
                                            loading={loading && activeTab === tab.key}
                                        />
                                        {currentData.count > RECORDS_PER_PAGE && (
                                            <div className="d-flex justify-content-center mt-3">
                                                <Pagination
                                                    activePage={page}
                                                    itemsCountPerPage={RECORDS_PER_PAGE}
                                                    totalItemsCount={currentData.count}
                                                    onChange={setPage}
                                                    itemClass="page-item"
                                                    linkClass="page-link"
                                                />
                                            </div>
                                        )}
                                    </TabPane>
                                ))}
                            </TabContent>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default ReminderDashboard;
